import { Checkbox, EmptyState, Grid, GridColumn, GridRow } from '@bp/ui-components';
import classNames from 'classnames';
import { useAssignments } from 'components/Assignments/hooks/useAssignments';
import { FileList } from 'components/Files/components/FileList/FileList';
import { useFiles } from 'components/Files/hooks/useFiles';
import { TipTapTextExtender } from 'components/TipTap/TipTapTextExtender';
import { useRequireParams } from 'hooks/useRequireParams';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { niceDate } from '../../../../utils/dateCalculations';
import { BpCard } from '../../../BpCard/BpCard';
import styles from './AssignmentTab.module.scss';

type AssignmentTabProps = {
  assignmentUuid: string;
};

export const AssignmentTab = ({ assignmentUuid }: AssignmentTabProps) => {
  const { t } = useTranslation();
  const { courseUuid } = useRequireParams(['courseUuid']);

  const [selectedUuids, setSelectedUuids] = useState<string[]>([]);

  const { assignments } = useAssignments({ courseUuid, assignmentUuid });
  const { handleDownloads } = useFiles();

  const assignment = assignments.find((a) => a.uuid === assignmentUuid);
  if (!assignment) throw new Error('Assignment not found');

  const material = assignment.material;
  const files = assignment.material?.files ?? [];

  return (
    <Grid className={styles['assignment-tab']}>
      <GridRow>
        <GridColumn width={8}>
          <GridRow>
            <BpCard header={{ headline: t('assignments.task') }}>
              {!material || !material.text ? (
                <EmptyState size='small' hideIcon subtitle={t('assignments.noTask')} />
              ) : (
                <TipTapTextExtender uuid={material.uuid} content={material.text} padding='none' />
              )}
            </BpCard>
          </GridRow>
          <GridRow>
            <BpCard
              noPadding
              bigActionsGap
              header={{
                headline: t('files.attachment', { count: 2 }),
                actions: [
                  {
                    text: t('files.downloadSelected'),
                    callback: () =>
                      handleDownloads(
                        files.filter((f) => selectedUuids.includes(f.uuid)),
                        `${t('files.title', { count: 2 })}_${assignment.title}`,
                      ),
                    disabled: selectedUuids.length === 0,
                  },
                  {
                    text: t('files.downloadAll'),
                    callback: () => handleDownloads(files, `${t('files.title', { count: 2 })}_${assignment.title}`),
                    disabled: files.length === 0,
                  },
                ],
              }}
            >
              <FileList
                actions={[]}
                files={files}
                downloadOnly
                onSelectionChange={(uuids) => setSelectedUuids(uuids)}
                showSelect
              />
            </BpCard>
          </GridRow>
        </GridColumn>
        <GridColumn width={4}>
          <BpCard className={styles.due} header={{ headline: t('assignments.dueDate') }}>
            <div className={styles.title}>{t('common.visibleFrom')}</div>
            <div className={styles.date}>
              {assignment.visibleFrom ? niceDate(assignment.visibleFrom, 'medium') : '-'}
            </div>
            <div className={classNames(styles.title, styles.second)}>{t('assignments.dueAt')}</div>
            <div className={styles.date}>{assignment.dueDate ? niceDate(assignment.dueDate, 'medium') : '-'}</div>
            <Checkbox
              className={styles.checkbox}
              label={t('submissions.allowAfterDue')}
              name='allowAfterDue'
              checked={assignment.allowSubmissionAfterDueDate}
              readonly
            />
          </BpCard>
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
